import { PANE_LABELS } from "~/state/types/canvas.types";
import { platformIsApple } from "~/utils/platform";

export const hotkeyCommands = [
	"new-tab",
	"bookmark",
	"bold",
	"convert-to-blockquote",
	"convert-to-code-block",
	"convert-to-h1",
	"convert-to-h2",
	"convert-to-h3",
	"convert-to-ordered_list",
	"convert-to-bullet_list",
	"convert-to-text",
	"copy-cell",
	"format-code",
	"italics",
	"open-components-tab",
	"open-cell-dependencies-tab",
	"run-cell-code",
	"save-canvas",
	"strikethrough",
	"toggle-document-history",
	"toggle-code-pane",
	"toggle-navigation-pane",
	"toggle-terminals",
	"underline",
	"switch-branch",
] as const;
export interface HotkeyInfo {
	mousetrapKeybinding(): string;
	prettyPrintKeybinding(): string;
	description: string;
	category: "System" | "Rich Text" | "Application";
	reserved?: boolean;
}

export const hotkeys: { [command in (typeof hotkeyCommands)[number]]: HotkeyInfo } = {
	/****************************************************************************************
	 * HOTKEY STYLE GUIDE
	 * ==================
	 * Follow these guidelines when adding new prettyPrint hotkeys to ensure they're displayed
	 * consistently in our app, and consistent with the user's OS.
	 *
	 * SYMBOLS:
	 * - ⌘: command
	 * - ⇧: shift
	 * - ⌥: option
	 * - ⌃: control
	 * - ↵: enter
	 *
	 * For MacOS:
	 * - Use the unicode symbols instead of spelling out the modifier key (e.g. ⌘ instead of "cmd")
	 * - Do NOT use a + symbol to join together multiple keys (e.g. ⌘G)
	 * - Show the modifier keys in the following order: control, option, shift, command
	 * - Use as few modifiers as possible, and in this order of preference: meta (command), shift, option, control
	 *
	 * For Windows:
	 * - Spell out the modifier keys instead of using symbols ("Shift" instead of ⇧)
	 * - Capitalize modifier keys
	 * - DO use the + symbold to join together modifier keys with no spaces (e.g. "Ctrl+B")
	 * - Use as few modifiers as possible, and in this order of preference: ctrl, alt, shift
	 ****************************************************************************************/

	/**
	 * RESERVED
	 * ========
	 * These hotkeys are not used in the app, but are reserved by the OS or browser.
	 * Setting `reserved: true` will cause tests to fail if we attempt to use one of
	 * these shortcuts for something in the Moment app. Do not use `reserved: true`
	 * to define shortcuts used in the app.
	 */
	"new-tab": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+t" : "ctrl+t"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘T" : "Ctrl+T"),
		description: "New Tab",
		category: "System",
		reserved: true,
	},

	/**
	 * RICH TEXT
	 * =========
	 * Used for editing rich text in the canvas.
	 */
	"bookmark": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+d" : "ctrl+d"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘D" : "Ctrl+D"),
		category: "Rich Text",
		description: "Bookmark",
	},
	"italics": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+i" : "ctrl+i"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘I" : "Ctrl+I"),
		category: "Rich Text",
		description: "Italics",
	},
	"underline": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+u" : "ctrl+u"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘U" : "Ctrl+U"),
		category: "Rich Text",
		description: "Underline",
	},
	"bold": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+b" : "ctrl+b"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘B" : "Ctrl+B"),
		category: "Rich Text",
		description: "Bold",
	},
	"strikethrough": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+x" : "ctrl+shift+x"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧X" : "Ctrl+Shift+X"),
		category: "Rich Text",
		description: "Strikethrough",
	},
	"convert-to-text": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+0" : "ctrl+shift+0"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧0" : "Ctrl+Shift+0"),
		category: "Rich Text",
		description: "Convert block to text",
	},
	"convert-to-h1": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+1" : "ctrl+shift+1"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧1" : "Ctrl+Shift+1"),
		category: "Rich Text",
		description: "Convert block to Heading 1",
	},
	"convert-to-h2": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+2" : "ctrl+shift+2"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧2" : "Ctrl+Shift+2"),
		category: "Rich Text",
		description: "Convert block to Heading 2",
	},
	"convert-to-h3": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+3" : "ctrl+shift+3"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧3" : "Ctrl+Shift+3"),
		category: "Rich Text",
		description: "Convert block to Heading 3",
	},
	"convert-to-ordered_list": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+7" : "ctrl+shift+7"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧7" : "Ctrl+Shift+7"),
		category: "Rich Text",
		description: "Convert block to ordered list",
	},
	"convert-to-bullet_list": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+8" : "ctrl+shift+8"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧8" : "Ctrl+Shift+8"),
		category: "Rich Text",
		description: "Convert block to bullet list",
	},
	"convert-to-blockquote": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+>" : "ctrl+shift+>"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧>" : "Ctrl+Shift+>"),
		category: "Rich Text",
		description: "Convert block to blockquote",
	},
	"convert-to-code-block": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+\\" : "ctrl+shift+\\"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧\\" : "Ctrl+Shift+\\"),
		category: "Rich Text",
		description: "Convert block to blockquote",
	},

	/**
	 * BLUE BAR
	 */
	"copy-cell": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+c" : "ctrl+c"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘C" : "Ctrl+C"),
		category: "Application",
		description: "Copy cell",
	},

	"format-code": {
		mousetrapKeybinding: () => (platformIsApple() ? "option+shift+f" : "alt+shift+f"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌥⇧F" : "Alt+Shift+F"),
		category: "Application",
		description: "Format code",
	},

	/**
	 * MOMENT APP
	 * ==========
	 * Used for app-chrome level actions like toggling panes
	 */
	"toggle-document-history": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+y" : "ctrl+y"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘Y" : "Ctrl+Y"),
		category: "Application",
		description: "Toggle open document history",
	},
	"toggle-code-pane": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+j" : "ctrl+j"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘J" : "Ctrl+J"),
		category: "Application",
		description: "Toggle code editor",
	},
	"toggle-navigation-pane": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+\\" : "ctrl+\\"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘\\" : "Ctrl+\\"),
		category: "Application",
		description: "Toggle Pages Pane",
	},
	"toggle-terminals": {
		mousetrapKeybinding: () => (platformIsApple() ? "ctrl+`" : "ctrl+`"),
		prettyPrintKeybinding: () => (platformIsApple() ? "Ctrl+`" : "Ctrl+`"),
		category: "Application",
		description: "Toggle Terminals",
	},
	"open-components-tab": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+p" : "ctrl+p"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘P" : "Ctrl+P"),
		category: "Application",
		description: `Toggle ${PANE_LABELS["components"].toLowerCase()}`,
	},
	"open-cell-dependencies-tab": {
		// meta+alt+d on toggles the macOS Dock, so use a different
		// modifier.
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+d" : "ctrl+alt+d"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⇧⌘D" : "Ctrl+Alt+D"),
		category: "Application",
		description: `Toggle ${PANE_LABELS["cellDependencies"].toLowerCase()}`,
	},
	"save-canvas": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+s" : "ctrl+s"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘S" : "Ctrl+S"),
		category: "Application",
		description: "Save canvas",
	},
	"run-cell-code": {
		mousetrapKeybinding: () => (platformIsApple() ? "shift+enter" : "shift+enter"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⇧↵" : "Shift+Enter"),
		category: "Application",
		description: "Run cell",
	},
	"switch-branch": {
		mousetrapKeybinding: () => (platformIsApple() ? "meta+shift+b" : "ctrl+shift+b"),
		prettyPrintKeybinding: () => (platformIsApple() ? "⌘⇧B" : "Ctrl+Shift+B"),
		category: "Application",
		description: "Run cell",
	},
} as const;
